import { FileText, Download, PieChart, Clock, Mail, MousePointerClick, Printer, FolderCheck, CheckCircle, ClipboardList } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  ProblemBlock,
  Step,
  BenefitCard,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const benefits = [
  {
    icon: FileText,
    title: "PDF professionnels",
    description: "Documents aux couleurs de votre entreprise, prêts à partager.",
    gradient: "from-[#14B8A6] to-[#0D9488]",
  },
  {
    icon: Download,
    title: "Export en un clic",
    description: "Générez et téléchargez instantanément, sans attente.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: PieChart,
    title: "Graphiques inclus",
    description: "Visualisations claires de la rentabilité et des coûts.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: Clock,
    title: "Historique complet",
    description: "Retrouvez tous vos rapports générés dans l'historique.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const steps = [
  {
    number: 1,
    title: "Sélectionnez un chantier",
    description: "Depuis votre liste de chantiers actifs ou terminés.",
    icon: MousePointerClick,
  },
  {
    number: 2,
    title: "Choisissez le type de rapport",
    description: "Suivi (en cours) ou Clôture (terminé).",
    icon: ClipboardList,
  },
  {
    number: 3,
    title: "Générez et téléchargez",
    description: "PDF professionnel en un clic, prêt à partager.",
    icon: Printer,
  },
];

const rapportSuiviItems = [
  "Résumé du chantier (client, dates, budget)",
  "État d'avancement (% réalisé)",
  "Budget consommé vs budget total",
  "Marge actuelle et projection",
  "Jours restants rentables (Jour Critique)",
  "Liste des dépenses (factures, frais, main d'œuvre)",
  "Équipe affectée et coûts",
  "Alertes en cours",
];

const rapportClotureItems = [
  "Synthèse financière complète",
  "Comparaison Devis initial vs Réalisé",
  "Marge finale (en euros et en %)",
  "Détail de toutes les dépenses par catégorie",
  "Historique des retards et suspensions",
  "Performance de l'équipe",
  "Recommandations IA",
  "Graphiques d'évolution",
];

const RapportsFeature = () => {
  usePageMeta({ title: "Rapports chantier PDF — Export et analyse | B8ild", description: "Générez des rapports PDF professionnels pour vos chantiers. Export financier, suivi de marge, analyse de rentabilité.", url: "https://b8ild.fr/product/rapports" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Rapports chantier PDF — Export et analyse | B8ild",
        description: "Générez des rapports PDF professionnels pour vos chantiers. Export financier, suivi de marge, analyse de rentabilité.",
        url: "https://b8ild.fr/product/rapports",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Rapports", item: "https://b8ild.fr/product/rapports" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Rapports & Documents" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Rapports & Documents"
        title="Vos bilans chantiers,"
        titleHighlight="professionnels et imprimables."
        subtitle="Générez des rapports de suivi et de clôture en un clic. PDF professionnels, prêts à partager avec vos clients ou votre comptable."
        icon={FileText}
        gradient="from-[#14B8A6] to-[#0D9488]"
      />

      {/* Problem */}
      <FeatureSection variant="dark">
        <ProblemBlock
          stat="2h+"
          text="perdues par chantier à compiler vos données dans Excel pour faire un point client ou clôturer un projet. Avec B8ild, un clic suffit."
          source="Temps moyen constaté"
        />
      </FeatureSection>

      {/* Les 2 types de rapports */}
      <FeatureSection
        title="Ce que B8ild génère"
        subtitle="Deux types de rapports pour chaque étape de votre chantier"
      >
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Rapport de Suivi */}
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-[#14B8A6]/30 to-transparent">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
                  <ClipboardList className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Rapport de Suivi</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] font-medium">
                    Chantier en cours
                  </span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Document PDF pour faire le point sur un chantier en cours :
              </p>
              <ul className="space-y-2">
                {rapportSuiviItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle className="h-4 w-4 text-[#14B8A6] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-white/[0.05]">
                <p className="text-xs text-white/40">
                  <span className="text-[#14B8A6] font-medium">Utilisation :</span> Point client, réunion interne, suivi hebdomadaire
                </p>
              </div>
            </div>
          </div>

          {/* Bilan de Clôture */}
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-[#F59E0B]/30 to-transparent">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
                  <FolderCheck className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Bilan de Clôture</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] font-medium">
                    Chantier terminé
                  </span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Document PDF complet pour archiver un chantier terminé :
              </p>
              <ul className="space-y-2">
                {rapportClotureItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle className="h-4 w-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-white/[0.05]">
                <p className="text-xs text-white/40">
                  <span className="text-[#F59E0B] font-medium">Utilisation :</span> Archivage, comptabilité, analyse post-projet
                </p>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* How it works */}
      <FeatureSection
        variant="dark"
        title="Comment ça marche ?"
        subtitle="En 3 étapes simples, générez vos rapports"
      >
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((step) => (
            <Step key={step.number} {...step} />
          ))}
        </div>
      </FeatureSection>

      {/* Benefits */}
      <FeatureSection
        title="Ce que vous gagnez"
        subtitle="Des bénéfices concrets pour votre activité"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} {...benefit} />
          ))}
        </div>

        {/* Bénéfices supplémentaires en liste */}
        <div className="max-w-3xl mx-auto mt-12">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Avantages concrets</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Gagnez 2h+ par chantier sur la préparation des bilans",
                  "Impressionnez vos clients avec des documents professionnels",
                  "Facilitez le travail de votre comptable",
                  "Gardez une trace de chaque chantier pour analyse future",
                  "Identifiez les patterns de rentabilité sur vos projets",
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white/70">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Mockup Preview */}
      <FeatureSection variant="dark" title="Aperçu d'un rapport">
        <div className="max-w-4xl mx-auto">
          {/* Mock PDF Report */}
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-white overflow-hidden shadow-2xl">
              {/* PDF Header */}
              <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#F59E0B] flex items-center justify-center">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold">B8ild</p>
                      <p className="text-white/50 text-xs">Rapport de suivi chantier</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm font-medium">Chantier : Rénovation Villa Martin</p>
                    <p className="text-white/50 text-xs">Généré le 25/01/2026</p>
                  </div>
                </div>
              </div>

              {/* PDF Content */}
              <div className="p-6 space-y-6">
                {/* KPIs */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Devis HT</p>
                    <p className="text-xl font-bold text-gray-900">45 000 €</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Dépensé</p>
                    <p className="text-xl font-bold text-[#F59E0B]">28 350 €</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Restant</p>
                    <p className="text-xl font-bold text-gray-900">16 650 €</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-xs text-green-600 uppercase tracking-wider">Marge</p>
                    <p className="text-xl font-bold text-green-600">+23.5%</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Avancement budget</span>
                    <span className="font-medium text-gray-900">63%</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full w-[63%] bg-gradient-to-r from-[#14B8A6] to-[#0D9488] rounded-full" />
                  </div>
                </div>

                {/* Mini chart placeholder */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-3">Évolution de la marge</p>
                  <div className="flex items-end justify-between h-16 gap-1">
                    {[40, 45, 42, 50, 55, 52, 58, 60, 55, 62, 65, 70].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-[#14B8A6]/50 to-[#14B8A6]"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-4 border-t border-gray-200 text-center">
                  <p className="text-xs text-gray-400">Document généré automatiquement par B8ild • www.b8ild.fr</p>
                </div>
              </div>
            </div>
          </div>

          {/* Download hint */}
          <div className="text-center mt-6">
            <p className="text-white/50 text-sm flex items-center justify-center gap-2">
              <Download className="h-4 w-4" />
              Téléchargeable en PDF haute qualité
            </p>
          </div>
        </div>
      </FeatureSection>

      {/* Pricing */}
      <FeatureSection
        title="Inclus dans tous les plans"
        subtitle="Générez autant de rapports que vous voulez"
      >
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { plan: "Starter", rapports: "Illimité", extra: "" },
              { plan: "Pro", rapports: "Illimité", extra: "" },
              { plan: "Entreprise", rapports: "Illimité", extra: "+ personnalisation logo" },
            ].map((item, index) => (
              <div
                key={index}
                className={`rounded-2xl p-[1px] ${
                  item.plan === "Pro"
                    ? "bg-gradient-to-b from-[#F59E0B]/50 to-transparent"
                    : "bg-gradient-to-b from-white/[0.1] to-transparent"
                }`}
              >
                <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 text-center h-full">
                  <p className="text-sm text-white/50 uppercase tracking-wider mb-2">{item.plan}</p>
                  <p className="text-2xl font-bold text-white mb-1">{item.rapports}</p>
                  {item.extra && (
                    <p className="text-xs text-[#F59E0B]">{item.extra}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Fin de trimestre. Le comptable vous tombe dessus.
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Il veut un etat des lieux par chantier. Le banquier veut un previsionnel de tresorerie. Le client du chantier avenue Foch veut un point d'avancement detaille.
              </p>
              <p>
                Sur Excel, chaque rapport prend 3 a 5 heures. Collecte des donnees, mise en forme, verification des chiffres. Et a chaque fois, des erreurs de copier-coller. Un zero en trop, une colonne decalee, un chantier oublie. Sur un rapport de 15 chantiers, la probabilite d'erreur depasse 30%.
              </p>
              <p>
                Avec B8ild : 2 clics. Selectionnez le chantier, cliquez sur "Exporter PDF". Rapport complet avec marges, couts detailles, avancement, et previsionnel. Donnees a jour a la seconde. En 1 an, c'est 150 a 200 heures economisees. A 50 EUR/h de cout salarial, c'est 7 500 a 10 000 EUR.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment faire un rapport de chantier ?",
                  a: "Un bon rapport de chantier contient : le budget initial, les couts engages (materiaux, main d'oeuvre, sous-traitance), la marge actuelle en % et en EUR, l'avancement physique, et le previsionnel de fin de chantier. B8ild genere tout ca en PDF en 2 clics. Les donnees sont a jour en temps reel — pas besoin de ressaisir.",
                },
                {
                  q: "Quel format pour un rapport de chantier : PDF ou Excel ?",
                  a: "PDF pour les clients et le banquier (propre, non modifiable, professionnel). Excel quand vous devez retravailler les chiffres vous-meme. B8ild genere du PDF pret a envoyer. Si votre comptable veut un format specifique, vous pouvez exporter les donnees brutes.",
                },
                {
                  q: "Faut-il un rapport par chantier ou un rapport global ?",
                  a: "Les deux. Le rapport par chantier sert au suivi operationnel et aux clients. Le rapport global montre la sante de l'entreprise : CA total, marge moyenne, chantiers en difficulte. Le banquier et le comptable veulent le global. Le chef de chantier veut le detail.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Prêt à générer vos premiers rapports ?"
        subtitle="Vos bilans chantiers n'ont jamais été aussi simples."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="rapports" />

      <LandingFooter />
    </div>
  );
};

export default RapportsFeature;
